import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from '@aws-amplify/auth';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import Loader from '../../components/loader/Loader';
import StatCard from '../../components/dashboard/StatCard';
import ChartCard from '../../components/dashboard/ChartCard';
import DashboardTabs from '../../components/dashboard/DashboardTabs';
import DashboardFilters from '../../components/dashboard/DashboardFilters';
import { baseOptions, doughnutOptions, CHART } from '../../components/dashboard/chartTheme';
import {
  formatNumber,
  formatDate,
  formatTimestamp,
  riskTone,
  riskRatioTone,
  frequencyLabel,
} from '../../components/dashboard/format';
import {
  fetchSelfHarmOverview,
  fetchSelfHarmUser,
  fetchSelfHarmAlerts,
  fetchSelfHarmTimeline,
} from '../../api/selfHarmApi';
import filterStyles from '../../components/dashboard/DashboardFilters.module.css';
import styles from '../../components/dashboard/Dashboard.module.css';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'alerts', label: 'Active Alerts' },
  { id: 'timeline', label: 'Timeline' },
];

const isoDaysAgo = (days) =>
  new Date(Date.now() - days * 86400000).toISOString().split('T')[0];

function SelfHarm() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [phone, setPhone] = useState('');
  const [startDate, setStartDate] = useState(isoDaysAgo(30));
  const [endDate, setEndDate] = useState(isoDaysAgo(0));
  const [hoursBack, setHoursBack] = useState(24);
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [userLimit, setUserLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        if (session.credentials) setAuthorized(true);
        else navigate('/admin/login');
      } catch (err) {
        navigate('/admin/login');
      }
    })();
  }, [navigate]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const range = { startDate, endDate };
      const cleanPhone = phone.trim();
      const [overviewRes, alertsRes, timelineRes] = await Promise.allSettled([
        cleanPhone
          ? fetchSelfHarmUser(cleanPhone, range)
          : fetchSelfHarmOverview(range),
        fetchSelfHarmAlerts({ hoursBack }),
        fetchSelfHarmTimeline(range),
      ]);
      if (overviewRes.status === 'rejected') throw overviewRes.reason;
      const overview = overviewRes.value;
      if (overview.error) throw new Error(overview.error);
      setData(overview);
      setAlerts(alertsRes.status === 'fulfilled' ? alertsRes.value : null);
      setTimeline(timelineRes.status === 'fulfilled' ? timelineRes.value : null);
    } catch (err) {
      setError(`Error loading dashboard: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [phone, startDate, endDate, hoursBack]);

  useEffect(() => {
    if (authorized) loadDashboard();
  }, [authorized]);

  const summary = data?.summary || {};
  const dailyData = data?.dailyData || [];

  const riskTrend = {
    labels: dailyData.map((d) => formatDate(d.date)),
    datasets: [
      {
        label: 'Total Detections',
        data: dailyData.map((d) => d.totalDetections || 0),
        borderColor: CHART.grey,
        tension: 0.4,
      },
      {
        label: 'Risk Detections',
        data: dailyData.map((d) => d.riskDetections || 0),
        borderColor: CHART.bad,
        backgroundColor: 'rgba(255, 58, 68, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const rb = data?.riskBreakdown || {};
  const riskDistribution = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [
      {
        data: [rb.critical_count || 0, rb.high_count || 0, rb.medium_count || 0, rb.low_count || 0],
        backgroundColor: [CHART.bad, CHART.avg, '#f1c40f', CHART.good],
        borderWidth: 3,
        borderColor: '#fff',
      },
    ],
  };

  const dt = data?.detectionTypeData || [];
  const detectionType = {
    labels: dt.map((d) => d.detectionType || 'Unknown'),
    datasets: [
      { label: 'Total', data: dt.map((d) => d.count || 0), backgroundColor: CHART.primary },
      { label: 'Risk', data: dt.map((d) => d.riskCount || 0), backgroundColor: CHART.bad },
    ],
  };

  const sev = data?.severityData || [];
  const severity = {
    labels: sev.map((d) => d.severity || 'Unknown'),
    datasets: [
      { label: 'Count', data: sev.map((d) => d.count || 0), backgroundColor: [CHART.bad, CHART.avg, CHART.good] },
    ],
  };

  const hp = data?.hourlyPatterns || [];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const hourlyRisk = {
    labels: hours.map((h) => `${h}:00`),
    datasets: [
      {
        label: 'Risk by Hour',
        data: hours.map((h) => (hp.find((x) => x.hour === h)?.riskCount) || 0),
        borderColor: CHART.bad,
        backgroundColor: 'rgba(255, 58, 68, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const urd = (data?.userRiskData || []).slice(0, 10);
  const userRisk = {
    labels: urd.map((u) => u.userName || 'Unknown'),
    datasets: [
      { label: 'Risk Detections', data: urd.map((u) => u.riskDetections || 0), backgroundColor: CHART.bad },
    ],
  };

  const alertSummary = {
    labels: ['Critical', 'High Risk', 'Other'],
    datasets: [
      {
        data: [
          alerts?.criticalAlerts || 0,
          alerts?.highRiskAlerts || 0,
          (alerts?.totalAlerts || 0) - (alerts?.criticalAlerts || 0) - (alerts?.highRiskAlerts || 0),
        ],
        backgroundColor: [CHART.bad, CHART.avg, CHART.grey],
        borderWidth: 3,
        borderColor: '#fff',
      },
    ],
  };

  const alertRows = alerts?.alerts || [];
  const timelineRows = timeline?.events || [];
  const topUsers = (data?.topRiskUsers || []).slice(0, userLimit);
  const detections = (data?.detailedData || []).slice(0, 50);

  const toneClass = (tone) =>
    tone === 'high' ? styles.badgeHigh : tone === 'medium' ? styles.badgeMedium : styles.badgeLow;
  const rankClass = (rank) =>
    rank === 1 ? styles.rank1 : rank === 2 ? styles.rank2 : rank === 3 ? styles.rank3 : '';

  const stats = [
    { label: 'Total Detections', value: formatNumber(summary.totalDetections) },
    { label: 'Risk Detections', value: formatNumber(summary.totalRiskDetections) },
    { label: 'Risk Ratio', value: `${summary.riskRatio || 0}%` },
    { label: 'Critical Alerts', value: formatNumber(summary.criticalAlerts), tone: 'high' },
    { label: 'High Risk Alerts', value: formatNumber(summary.highRiskAlerts), tone: 'high' },
    { label: 'Medium Risk Alerts', value: formatNumber(summary.mediumRiskAlerts), tone: 'medium' },
    { label: 'Low Risk Alerts', value: formatNumber(summary.lowRiskAlerts), tone: 'low' },
    { label: 'Users at Risk', value: formatNumber(summary.uniqueUsersAtRisk) },
    { label: 'Avg Risk Score', value: (summary.averageRiskScore || 0).toFixed(1) },
    { label: 'Total Users', value: formatNumber(summary.uniqueUsersTotal) },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Self-Harm Detection</h1>
        <p>Risk monitoring and analysis for user safety.</p>
      </div>

      <DashboardFilters
        phone={phone}
        startDate={startDate}
        endDate={endDate}
        onPhoneChange={setPhone}
        onStartDate={setStartDate}
        onEndDate={setEndDate}
        onApply={loadDashboard}
        loading={loading}
      >
        <div className={filterStyles.group}>
          <label>Alert Range</label>
          <select value={hoursBack} onChange={(e) => setHoursBack(Number(e.target.value))}>
            <option value={24}>Last 24 hours</option>
            <option value={48}>Last 48 hours</option>
            <option value={72}>Last 72 hours</option>
            <option value={168}>Last week</option>
          </select>
        </div>
      </DashboardFilters>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <DashboardTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {loading ? (
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <>
              <div className={styles.statsGrid}>
                {stats.map((s) => (
                  <StatCard key={s.label} label={s.label} value={s.value} tone={s.tone} />
                ))}
              </div>
              <div className={styles.chartsGrid}>
                <ChartCard title="Daily Risk Trend" wide>
                  <Line data={riskTrend} options={baseOptions()} />
                </ChartCard>
              </div>
            </>
          )}

          {activeTab === 'analytics' && (
            <div className={styles.chartsGrid}>
              <ChartCard title="Risk Level Distribution">
                <Doughnut data={riskDistribution} options={doughnutOptions()} />
              </ChartCard>
              <ChartCard title="Detection Type Analysis">
                <Bar data={detectionType} options={baseOptions()} />
              </ChartCard>
              <ChartCard title="Severity Breakdown">
                <Bar data={severity} options={baseOptions()} />
              </ChartCard>
              <ChartCard title="Hourly Risk Patterns">
                <Line data={hourlyRisk} options={baseOptions()} />
              </ChartCard>
              <ChartCard title="User Risk Analysis" wide>
                <Bar data={userRisk} options={baseOptions()} />
              </ChartCard>
            </div>
          )}

          {activeTab === 'alerts' && (
            <>
              <div className={styles.chartsGrid}>
                <ChartCard title="Alert Summary">
                  <Doughnut data={alertSummary} options={doughnutOptions()} />
                </ChartCard>
              </div>
              <div className={styles.tableCard}>
                <div className={styles.tableHeader}><span>Active High-Risk Alerts</span></div>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Timestamp</th><th>User</th><th>Risk Level</th><th>Severity</th>
                        <th>Detection Type</th><th>Content</th><th>Hours Ago</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alertRows.length ? (
                        alertRows.map((a, i) => (
                          <tr key={i}>
                            <td>{formatTimestamp(a.timestamp)}</td>
                            <td>{a.userName || 'Unknown'}</td>
                            <td><span className={`${styles.badge} ${toneClass(riskTone(a.riskLevel))}`}>{a.riskLevel || 'Unknown'}</span></td>
                            <td>{a.severity || 'Unknown'}</td>
                            <td>{a.detectionType || 'Unknown'}</td>
                            <td title={a.flaggedContent}>{a.flaggedContent || 'N/A'}</td>
                            <td>{a.hoursAgo || 0}h ago</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td className={styles.empty} colSpan={7}>No active alerts.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === 'timeline' && (
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}><span>Risk Event Timeline</span></div>
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Timestamp</th><th>User</th><th>Risk Level</th>
                      <th>Severity</th><th>Detection Type</th><th>Content</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timelineRows.length ? (
                      timelineRows.map((e, i) => (
                        <tr key={i}>
                          <td>{formatTimestamp(e.timestamp)}</td>
                          <td>{e.userName || 'Unknown'}</td>
                          <td><span className={`${styles.badge} ${toneClass(riskTone(e.riskLevel))}`}>{e.riskLevel || 'Unknown'}</span></td>
                          <td>{e.severity || 'Unknown'}</td>
                          <td>{e.detectionType || 'Unknown'}</td>
                          <td title={e.flaggedContent}>{e.flaggedContent || 'N/A'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td className={styles.empty} colSpan={6}>No timeline events.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <span>Top Risk Users</span>
              <select
                className={styles.topSelect}
                value={userLimit}
                onChange={(e) => setUserLimit(Number(e.target.value))}
              >
                {[5, 10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>{`Top ${n}`}</option>
                ))}
              </select>
            </div>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Rank</th><th>User</th><th>Phone</th><th>Risk Detections</th>
                    <th>Total</th><th>Risk Ratio</th><th>Total Score</th>
                    <th>Avg Score</th><th>Frequency</th><th>Last Event</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.length ? (
                    topUsers.map((u, i) => {
                      const rank = i + 1;
                      const freq = frequencyLabel(u.riskDetections || 0);
                      return (
                        <tr key={i}>
                          <td><span className={`${styles.rank} ${rankClass(rank)}`}>{rank}</span></td>
                          <td>{u.userName || 'Unknown'}</td>
                          <td>{u.phoneNumber || 'N/A'}</td>
                          <td><strong>{formatNumber(u.riskDetections)}</strong></td>
                          <td>{formatNumber(u.totalDetections)}</td>
                          <td><span className={`${styles.badge} ${toneClass(riskRatioTone(u.riskRatio || 0))}`}>{(u.riskRatio || 0).toFixed(1)}%</span></td>
                          <td>{(u.totalRiskScore || 0).toFixed(1)}</td>
                          <td>{(u.averageRiskScore || 0).toFixed(1)}</td>
                          <td><span className={`${styles.badge} ${toneClass(freq.tone)}`}>{freq.label}</span></td>
                          <td>{formatTimestamp(u.lastRiskEvent)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr><td className={styles.empty} colSpan={10}>No data available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.tableCard}>
            <div className={styles.tableHeader}><span>Detection Details</span></div>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Timestamp</th><th>User</th><th>Risk Level</th><th>Severity</th>
                    <th>Detection Type</th><th>On Risk</th><th>Content</th><th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {detections.length ? (
                    detections.map((d, i) => (
                      <tr key={i}>
                        <td>{formatTimestamp(d.createdAt || d.timestamp)}</td>
                        <td>{d.userName || 'Unknown'}</td>
                        <td><span className={`${styles.badge} ${toneClass(riskTone(d.riskLevel))}`}>{d.riskLevel || 'Unknown'}</span></td>
                        <td>{d.severity || 'Unknown'}</td>
                        <td>{d.detectionType || 'Unknown'}</td>
                        <td><span className={`${styles.badge} ${d.onRisk ? styles.badgeHigh : styles.badgeLow}`}>{d.onRisk ? 'Yes' : 'No'}</span></td>
                        <td title={d.flaggedContent}>{d.flaggedContent || 'N/A'}</td>
                        <td>{d.riskScore || 0}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td className={styles.empty} colSpan={8}>No detections.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SelfHarm;
